import dayjs from "dayjs";
import {
  Alert,
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { formatGbp } from "../../helpers/currency";
import { mediaUrl } from "../../helpers/mediaHelper";

const REFUND_STATUS_COLOR = {
  pending: "warning",
  processing: "primary",
  approved: "success",
  completed: "success",
  rejected: "danger",
  cancelled: "secondary",
};

function isImageProof(path) {
  return /\.(jpe?g|png|gif|webp)$/i.test(String(path || ""));
}

const RefundDetailModal = ({ isOpen, toggle, refund }) => {
  if (!refund) return null;

  const proofUrl = refund.proof ? mediaUrl(refund.proof) : "";
  const statusColor = REFUND_STATUS_COLOR[refund.status] || "info";

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Refund details</ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <h5 className="mb-1">{refund.slug || "—"}</h5>
            <p className="text-muted small mb-0">
              Requested{" "}
              {refund.createdAt
                ? dayjs(refund.createdAt).format("DD MMM YYYY HH:mm")
                : "—"}
            </p>
            {refund.updatedAt && refund.updatedAt !== refund.createdAt ? (
              <p className="text-muted small mb-0">
                Updated{" "}
                {dayjs(refund.updatedAt).format("DD MMM YYYY HH:mm")}
              </p>
            ) : null}
          </div>
          <Badge className={`bg-${statusColor} text-uppercase`}>
            {refund.status || "unknown"}
          </Badge>
        </div>

        <Row className="g-3 mb-3">
          <Col sm={6}>
            <p className="text-muted small mb-1">Requested amount</p>
            <p className="fw-semibold mb-0">{formatGbp(refund.amount)}</p>
          </Col>
          <Col sm={6}>
            <p className="text-muted small mb-1">Amount to refund</p>
            <p className="fw-semibold mb-0">
              {refund.amountToRefund != null && refund.amountToRefund !== ""
                ? formatGbp(refund.amountToRefund)
                : "—"}
            </p>
          </Col>
        </Row>

        <div className="border rounded p-3 mb-3 bg-light">
          <h6 className="text-muted mb-2">Bank details</h6>
          <p className="mb-1 small">
            <span className="text-muted">Account holder: </span>
            {refund.accountHolderName || "—"}
          </p>
          <p className="mb-1 small">
            <span className="text-muted">Bank: </span>
            {refund.bankName || "—"}
          </p>
          <p className="mb-1 small">
            <span className="text-muted">Sort code: </span>
            {refund.sortCode || "—"}
          </p>
          <p className="mb-0 small">
            <span className="text-muted">Account number: </span>
            {refund.accountNumber || "—"}
          </p>
        </div>

        {refund.status === "rejected" && refund.rejectionReason ? (
          <Alert color="danger" className="py-2 small">
            <strong>Rejection reason</strong>
            <p className="mb-0 mt-1">{refund.rejectionReason}</p>
          </Alert>
        ) : null}

        {refund.status === "completed" ? (
          <div>
            <h6 className="text-muted mb-2">Proof of refund</h6>
            {proofUrl ? (
              <div>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <a
                    href={proofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-soft-primary"
                  >
                    <i className="bx bx-link-external me-1" />
                    Open proof
                  </a>
                </div>
                {isImageProof(refund.proof) ? (
                  <img
                    src={proofUrl}
                    alt="Refund proof"
                    className="img-fluid rounded border"
                    style={{ maxHeight: 320 }}
                  />
                ) : null}
              </div>
            ) : (
              <p className="text-muted small mb-0">No proof file available.</p>
            )}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RefundDetailModal;
